import { FunctionComponent, HTMLAttributes, ReactNode, useMemo } from 'react';
import * as yup from 'yup';
import { FieldValues, FormState, SubmitHandler, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js';

import { FormError, Select, Textarea } from '../../components/ui';

import { useSupportRequest } from '../../hooks/api/useSupport';
import useAuth from '../../hooks/utils/useAuth';

type SupportCategoryType = {
  label: string;
  group: string;
};

const supportCategories: Record<string, SupportCategoryType> = {
  suggestion: {
    label: 'Suggestion',
    group: 'PRODUCT',
  },
  payment_concern: {
    label: 'Payment Concern',
    group: 'FINANCE',
  },
  call_connection: {
    label: 'Call Connection Issue',
    group: 'TECHNOLOGY',
  },
  listener_praise: {
    label: 'Listener Praise',
    group: 'FEEDBACK',
  },
  listener_message_concern: {
    label: 'Listener Message Concern',
    group: 'REPORT USER',
  },
  listener_call_concern: {
    label: 'Listener Call Concern',
    group: 'REPORT USER',
  },
};

const callerMenuReasons = [
  'suggestion',
  'payment_concern',
  'call_connection',
  'listener_praise',
  'listener_message_concern',
];

const callerOfCallReasons = ['listener_praise', 'listener_call_concern', 'suggestion', 'call_connection'];

export interface SupportFormProps extends HTMLAttributes<HTMLFormElement> {
  topActionButton?: (formState: FormState<FieldValues>) => ReactNode;
  bottomActionButton?: (formState: FormState<FieldValues>) => ReactNode;
  onSuccessSubmitted?: () => void;
  option?: 'menu' | 'call-report' | string | null;
  callReport?: string | null;
  callId?: string | null;
  contactUs?: string | null;
}

export const SupportForm: FunctionComponent<SupportFormProps> = ({
  topActionButton,
  bottomActionButton,
  onSuccessSubmitted,
  option,
  callReport,
  callId,
  contactUs,
  className,
  ...props
}) => {
  const { user } = useAuth();
  const supportRequestMutation = useSupportRequest();

  const { register, handleSubmit, reset, formState } = useForm({
    resolver: yupResolver(supportSchema()),
    mode: 'onTouched',
  });
  const { errors } = formState;

  const { topActionButtonElement, bottomActionButtonElement } = useMemo(
    () => ({
      topActionButtonElement: topActionButton ? topActionButton(formState) : null,
      bottomActionButtonElement: bottomActionButton ? bottomActionButton(formState) : null,
    }),
    [topActionButton, bottomActionButton, formState],
  );

  const { supportCategoryOptions, context } = useMemo(() => {
    return {
      supportCategoryOptions: (option === 'call-report' ? callerOfCallReasons : callerMenuReasons).filter((item) =>
        callReport ? ['listener_praise', 'call_connection', 'listener_call_concern'].includes(item) : true,
      ),
      context: option === 'call-report' ? 'caller_of_call' : 'menu',
    };
  }, [option, callReport]);

  const submit: SubmitHandler<
    Partial<{
      supportCategory: string;
      comments: string;
    }>
  > = ({ supportCategory = '', comments = '' }) => {
    if (user?.id) {
      if (option === 'call-report') {
        localStorage.setItem('call-report-submit', callId ? callId : '0');
      }

      return new Promise((res) =>
        supportRequestMutation.mutate(
          {
            userId: user?.id,
            input: {
              userId: user?.id,
              group: supportCategories[supportCategory].group,
              subject: supportCategories[supportCategory].label,
              context: contactUs === 'login' ? contactUs : context,
              comments: comments,
              call_id: callId ? callId : undefined,
              platform: 'User Agent',
              // TODO: inject useful metadata see https://listenersoncall.atlassian.net/browse/LOC-3110
              build: Math.floor(Math.random() * 1_000_000_000_000),
            },
          },
          {
            onSuccess: () => {
              reset();
              res(null);

              if (onSuccessSubmitted) {
                onSuccessSubmitted();
              }
            },
          },
        ),
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(submit)} {...props} className={twMerge('', className)}>
      {topActionButtonElement}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label className="font-['Open_Sans'] text-sm font-semibold leading-5 text-black">
            What is this about? <span className="text-red-500">*</span>
          </label>
          <Select
            {...register('supportCategory')}
            label=""
            isRequired
            className={twMerge(
              'border-avatar-border-slate rounded-lg border bg-white font-["Open_Sans"] text-base',
              errors?.supportCategory?.message && 'border-red-500',
            )}
            options={
              supportCategoryOptions.map((item) => ({
                label: supportCategories[item].label,
                value: item,
              })) || []
            }
          />
          {errors?.supportCategory?.message && (
            <FormError text={errors?.supportCategory?.message as string} className="mt-1" />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-['Open_Sans'] text-sm font-semibold leading-5 text-black">
            Message <span className="text-red-500">*</span>
          </label>
          <Textarea
            {...register('comments')}
            className="border-avatar-border-slate rounded-lg border bg-white font-['Open_Sans'] text-base"
            placeholder="Please share what you’re experiencing or any feedback you’d like us to consider. The more detail you can provide, the easier it is for us to help."
            hasError={!!errors?.comments?.message}
            minRows={7}
            maxRows={12}
          />
          {errors?.comments?.message && <FormError text={errors?.comments?.message as string} className="mt-1" />}
        </div>
      </div>
      {bottomActionButtonElement}
    </form>
  );
};

function supportSchema() {
  return yup.object().shape({
    supportCategory: yup.string().trim().required('Support category is required.'),
    comments: yup.string().trim().required('Message cannot be empty.'),
  });
}
